import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useEffectEvent, useReducer } from 'react';

import { useRailFlyout } from './useRailFlyout';

type SearchFlyoutState = {
  isOpen: boolean;
  trigger: HTMLButtonElement | null;
  hoverPanel: HTMLDivElement | null;
  panel: HTMLDivElement | null;
  isSearchPending: boolean;
  searchFocus: { panel: HTMLDivElement } | null;
  focusBeforeHover: HTMLElement | null;
  focusRestore: { target: HTMLElement; trigger: HTMLButtonElement | null; hoverPanel: HTMLDivElement | null } | null;
};

type SearchFlyoutAction =
  | { type: 'open'; focused: Element | null }
  | { type: 'close' }
  | { type: 'requestSearch' }
  | { type: 'triggerMounted'; trigger: HTMLButtonElement | null }
  | { type: 'hoverPanelMounted'; hoverPanel: HTMLDivElement | null }
  | { type: 'panelMounted'; panel: HTMLDivElement | null };

const initialState: SearchFlyoutState = {
  isOpen: false,
  trigger: null,
  hoverPanel: null,
  panel: null,
  isSearchPending: false,
  searchFocus: null,
  focusBeforeHover: null,
  focusRestore: null,
};

function withSearchRequested(state: SearchFlyoutState): SearchFlyoutState {
  if (state.panel) {
    return { ...state, isSearchPending: false, searchFocus: { panel: state.panel } };
  }
  return { ...state, isSearchPending: true };
}

function reducer(state: SearchFlyoutState, action: SearchFlyoutAction): SearchFlyoutState {
  switch (action.type) {
    case 'open': {
      const isHoverOpen = action.focused !== state.trigger;
      const isInRail = !!state.trigger?.closest('[data-pane="rail"]')?.contains(action.focused);
      const opened = {
        ...state,
        isOpen: true,
        focusRestore: null,
        focusBeforeHover: isHoverOpen && !isInRail && action.focused instanceof HTMLElement ? action.focused : null,
      };
      return isHoverOpen ? withSearchRequested(opened) : opened;
    }
    case 'close':
      return {
        ...state,
        isOpen: false,
        isSearchPending: false,
        searchFocus: null,
        focusBeforeHover: null,
        focusRestore: state.focusBeforeHover
          ? { target: state.focusBeforeHover, trigger: state.trigger, hoverPanel: state.hoverPanel }
          : null,
      };
    case 'requestSearch':
      return withSearchRequested(state);
    case 'triggerMounted':
      return action.trigger === state.trigger ? state : { ...state, trigger: action.trigger };
    case 'hoverPanelMounted':
      return action.hoverPanel === state.hoverPanel ? state : { ...state, hoverPanel: action.hoverPanel };
    case 'panelMounted':
      if (action.panel === state.panel) {
        return state;
      }
      if (action.panel && state.isSearchPending) {
        return { ...state, panel: action.panel, isSearchPending: false, searchFocus: { panel: action.panel } };
      }
      return { ...state, panel: action.panel };
    default:
      return state;
  }
}

function focusSearch(panel: HTMLElement) {
  return window.requestAnimationFrame(() => {
    panel.querySelector<HTMLInputElement>('input')?.focus();
  });
}

export function useRailSearchFlyout({ onOpen }: { onOpen?: () => void } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const flyout = useRailFlyout(state.trigger, state.panel, { hoverPanel: state.hoverPanel });
  const { isOpen } = flyout;
  const notifyOpen = useEffectEvent(() => onOpen?.());
  const setTrigger = useCallback(
    (trigger: HTMLButtonElement | null) => dispatch({ type: 'triggerMounted', trigger }),
    [],
  );
  const setHoverPanel = useCallback(
    (hoverPanel: HTMLDivElement | null) => dispatch({ type: 'hoverPanelMounted', hoverPanel }),
    [],
  );
  const setPanel = useCallback((panel: HTMLDivElement | null) => dispatch({ type: 'panelMounted', panel }), []);
  const requestSearch = useCallback(() => dispatch({ type: 'requestSearch' }), []);
  const requestSearchFromKey = useCallback((event: { key: string }) => {
    if (event.key === 'Enter' || event.key === ' ') {
      dispatch({ type: 'requestSearch' });
    }
  }, []);

  const { close } = flyout;
  const closeFromEmptySearch = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && event.target instanceof HTMLInputElement && event.target.value === '') {
        event.stopPropagation();
        close();
      }
    },
    [close],
  );

  if (state.isOpen !== isOpen) {
    dispatch(isOpen ? { type: 'open', focused: document.activeElement } : { type: 'close' });
  }

  useEffect(() => {
    if (isOpen) {
      notifyOpen();
    }
  }, [isOpen]);

  const { searchFocus } = state;
  useEffect(() => {
    if (!searchFocus) {
      return;
    }
    const frame = focusSearch(searchFocus.panel);
    return () => window.cancelAnimationFrame(frame);
  }, [searchFocus]);

  const { focusRestore } = state;
  useEffect(() => {
    if (!focusRestore?.target.isConnected) {
      return;
    }
    const active = focusRestore.target.ownerDocument.activeElement;
    const isFocusLeftBehind =
      !active ||
      active === active.ownerDocument.body ||
      active === focusRestore.trigger ||
      !!focusRestore.hoverPanel?.contains(active);
    if (isFocusLeftBehind) {
      focusRestore.target.focus();
    }
  }, [focusRestore]);

  return {
    isOpen,
    close,
    triggerProps: { isOpen, onOpenChange: flyout.onOpenChange },
    buttonProps: { ref: setTrigger, onPress: requestSearch, onKeyDown: requestSearchFromKey },
    hoverPanelProps: { ref: setHoverPanel, onKeyDownCapture: closeFromEmptySearch },
    panelProps: { ref: setPanel },
  };
}
